import { useCallback, useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import {
    ESTADO_COBRANCA_LABEL,
    ESTADO_COBRANCA_TONE,
    METODOS_PAGAMENTO,
    METODO_PAGAMENTO_LABEL,
    formatDate,
    formatMoney,
    type Cobranca,
    type MetodoPagamento,
    type Pagamento,
} from '../subscricoesModel'

interface Props {
    cobrancaId: string | null
    onClose: () => void
    onChanged: () => void
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }} component="div">{children}</Typography>
        </Box>
    )
}

export default function CobrancaDetailDialog({ cobrancaId, onClose, onChanged }: Props) {
    const { notify } = useNotification()

    const [cobranca, setCobranca] = useState<Cobranca | null>(null)
    const [pagamentos, setPagamentos] = useState<Pagamento[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const [valor, setValor] = useState('')
    const [pagoEm, setPagoEm] = useState('')
    const [metodo, setMetodo] = useState<MetodoPagamento>('TRANSFERENCIA')
    const [referencia, setReferencia] = useState('')
    const [notas, setNotas] = useState('')
    const [saving, setSaving] = useState(false)

    const carregar = useCallback(() => {
        if (!cobrancaId) return
        setLoading(true)
        setError('')
        Promise.all([
            api.get<Cobranca>(`/api/admin/subscricoes/cobrancas/${cobrancaId}`),
            api.get<Pagamento[]>(`/api/admin/subscricoes/cobrancas/${cobrancaId}/pagamentos`),
        ])
            .then(([c, p]) => {
                setCobranca(c.data)
                setPagamentos(Array.isArray(p.data) ? p.data : [])
                setValor(String(c.data.emDivida))
            })
            .catch(() => setError('Não foi possível carregar a cobrança.'))
            .finally(() => setLoading(false))
    }, [cobrancaId])

    useEffect(() => {
        if (!cobrancaId) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset antes de (re)carregar a cobrança seleccionada
        setCobranca(null)
        setPagamentos([])
        setValor('')
        setPagoEm('')
        setMetodo('TRANSFERENCIA')
        setReferencia('')
        setNotas('')
        carregar()
    }, [cobrancaId, carregar])

    const registar = async () => {
        if (!cobranca) return
        const valorNum = Number(valor)
        if (valor.trim() === '' || Number.isNaN(valorNum) || valorNum <= 0) { notify('Valor inválido.', 'error'); return }
        setSaving(true)
        try {
            await api.post(`/api/admin/subscricoes/cobrancas/${cobranca.id}/pagamentos`, {
                valor: valorNum,
                pagoEm: pagoEm || null,
                metodo,
                referencia: referencia.trim() || null,
                notas: notas.trim() || null,
            })
            notify('Pagamento registado.')
            setReferencia('')
            setNotas('')
            carregar()
            onChanged()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível registar o pagamento.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const cancelar = async () => {
        if (!cobranca) return
        if (!window.confirm(`Cancelar a cobrança ${cobranca.referencia}?`)) return
        try {
            await api.post(`/api/admin/subscricoes/cobrancas/${cobranca.id}/cancelar`)
            notify('Cobrança cancelada.')
            carregar()
            onChanged()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível cancelar.', 'error')
        }
    }

    const podeRegistar = cobranca && (cobranca.estado === 'PENDENTE' || cobranca.estado === 'VENCIDA')

    return (
        <Dialog open={cobrancaId !== null} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {cobranca ? `Cobrança ${cobranca.referencia}` : 'Cobrança'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

                {cobranca && !loading && (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <Field label="Empresa">{cobranca.tenantNome ?? cobranca.tenantId}</Field>
                            <Field label="Estado">
                                <Chip size="small" label={ESTADO_COBRANCA_LABEL[cobranca.estado]} sx={{ fontWeight: 600, ...ESTADO_COBRANCA_TONE[cobranca.estado] }} />
                            </Field>
                            <Field label="Período">{formatDate(cobranca.periodoInicio)} – {formatDate(cobranca.periodoFim)}</Field>
                            <Field label="Origem">{cobranca.origem === 'AUTOMATICA' ? 'Automática' : 'Manual'}</Field>
                            <Field label="Valor">{formatMoney(cobranca.valor)}</Field>
                            <Field label="Em dívida">{formatMoney(cobranca.emDivida)}</Field>
                            <Field label="Emitida">{formatDate(cobranca.emitidaEm)}</Field>
                            <Field label="Vencimento">{formatDate(cobranca.vencimentoEm)}</Field>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Pagamentos ({pagamentos.length})
                            </Typography>
                            {pagamentos.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Sem pagamentos registados.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Data</TableCell>
                                            <TableCell>Método</TableCell>
                                            <TableCell>Referência</TableCell>
                                            <TableCell align="right">Valor</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagamentos.map((p) => (
                                            <TableRow key={p.id}>
                                                <TableCell>{formatDate(p.pagoEm)}</TableCell>
                                                <TableCell>{METODO_PAGAMENTO_LABEL[p.metodo]}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{p.referencia ?? '—'}</TableCell>
                                                <TableCell align="right">{formatMoney(p.valor)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>

                        {podeRegistar && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Registar pagamento</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                                label="Valor"
                                                type="number"
                                                value={valor}
                                                onChange={(e) => setValor(e.target.value)}
                                                size="small"
                                                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                label="Data"
                                                type="date"
                                                value={pagoEm}
                                                onChange={(e) => setPagoEm(e.target.value)}
                                                size="small"
                                                helperText="Vazio = hoje"
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                                select
                                                label="Método"
                                                value={metodo}
                                                onChange={(e) => setMetodo(e.target.value as MetodoPagamento)}
                                                size="small"
                                                sx={{ flex: 1 }}
                                            >
                                                {METODOS_PAGAMENTO.map((m) => <MenuItem key={m} value={m}>{METODO_PAGAMENTO_LABEL[m]}</MenuItem>)}
                                            </TextField>
                                            <TextField
                                                label="Referência"
                                                value={referencia}
                                                onChange={(e) => setReferencia(e.target.value)}
                                                size="small"
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                        <TextField
                                            label="Notas"
                                            value={notas}
                                            onChange={(e) => setNotas(e.target.value)}
                                            size="small"
                                            multiline
                                            minRows={2}
                                        />
                                        <Button variant="contained" onClick={registar} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                                            {saving ? 'A registar…' : 'Registar pagamento'}
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {cobranca && cobranca.estado !== 'PAGA' && cobranca.estado !== 'CANCELADA' && (
                    <Button color="error" onClick={cancelar} sx={{ mr: 'auto' }}>Cancelar cobrança</Button>
                )}
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
